<template>
  <div v-if="receiverCandidates.length === 0" class="mt-4">
    You have not registered any suppliers. Please import your suppliers or add
    them manually.
  </div>
  <div v-else>
    <v-row v-if="senderIsTlc">
      <v-col class="text-left">
        <p>1. Select a date to request that your suppliers respond by:</p>
        <div class="ml-4 mt-2 mb-4">
          <PDatePicker
            :default-date="selectedDate"
            @date-updated="$emit('dueDateUpdated', $event)"
          />
        </div>
        <p class="ml-4">
          Automatic reminders are sent five days before the deadline and one day
          after the deadline.
        </p>
      </v-col>
    </v-row>
    <v-row class="mb-5">
      <v-col>
        <p>
          <span v-if="senderIsTlc">2. </span>Please select your suppliers before
          previewing the email that will be sent on your behalf.
        </p>
      </v-col>
      <v-col class="text-right">
        <div class="d-flex justify-end">
          <ImportSuppliers @suppliers-uploaded="$emit('suppliersUpdated')" />
          <AddSupplier
            class="ml-2"
            :can-upload-business-partner="senderIsTlc"
            @supplier-added="$emit('suppliersUpdated')"
          />
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <o-table
          v-if="hasItems"
          class="ma-2"
          v-model:checked-rows="selectedReceiverCandidates"
          :data="mappedData"
          :loading="isLoading"
          hoverable
          checkable
          paginated
          searchable
          :per-page="100"
        >
          <o-table-column v-for="column in columns" v-bind="column">
            <template v-if="column.searchable" #searchable="props">
              <o-input
                v-model="props.filters[props.column.field]"
                placeholder="Search..."
                size="small"
              />
            </template>
            <template #default="props">
              {{ props.row[column.field] }}
            </template>
          </o-table-column>
        </o-table>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import PDatePicker from "@/components/custom/PDatePicker.vue";
import { getTextForDistributionStatus } from "@/models/enum/distributionStatus";
import AddSupplier from "@/views/supplier-registry/AddSupplier.vue";
import ImportSuppliers from "@/views/supplier-registry/ImportSuppliers.vue";
import { ref, computed, watch, type Ref } from "vue";
import { ReceiverCandidate } from "@/models/receiverCandidate";

interface DisplayReceiverCandidate {
  companyName: string;
  email: string;
  businessRelationship: string;
  statusText: string;
  emailsSent: number;
}

const props = defineProps<{
  senderIsTlc: boolean;
  receiverCandidates: Array<ReceiverCandidate>;
  selected: Array<ReceiverCandidate>;
  selectedDate: Date;
}>();

const emit = defineEmits<{
  (e: "receiversUpdated", receivers: Array<ReceiverCandidate>): void;
  (e: "dueDateUpdated", dueDate: Date): void;
  (e: "suppliersUpdated"): void;
}>();

const mappedData = computed(() =>
  props.receiverCandidates.map(
    (r) =>
      ({
        companyName: r.companyName,
        email: r.email,
        businessRelationship: r.isBusinessPartner
          ? "Business partner"
          : "Supplier",
        statusText: getTextForDistributionStatus(r.distributionStatus),
        emailsSent: r.emailsSent,
      } as DisplayReceiverCandidate)
  )
);

const selectedReceiverCandidates: Ref<DisplayReceiverCandidate[]> = ref(
  initializeSelectedReceiverCandidates()
);

function initializeSelectedReceiverCandidates(): DisplayReceiverCandidate[] {
  if (props.selected.length == 0) {
    return new Array<DisplayReceiverCandidate>();
  }

  var validReceiverCandidates = new Array<DisplayReceiverCandidate>();

  props.selected.forEach((r) => {
    const i = mappedData.value.findIndex((x) => x.email == r.email);
    if (i != -1) {
      validReceiverCandidates.push(mappedData.value[i]);
    }
  });
  return validReceiverCandidates;
}

const isLoading = ref(false);

const hasItems = computed(
  () => props.receiverCandidates != null && props.receiverCandidates.length > 0
);

watch(
  selectedReceiverCandidates,
  async (
    newReceivers: DisplayReceiverCandidate[],
    oldReceivers: DisplayReceiverCandidate[]
  ) => {
    const correspondingReceiverCandidates = props.receiverCandidates.filter(
      (r) => newReceivers.findIndex((x) => x.email == r.email) != -1
    );
    emit("receiversUpdated", correspondingReceiverCandidates);
  }
);

const columns = ref([
  {
    field: "companyName",
    label: "Company",
    searchable: true,
    sortable: true,
  },
  {
    field: "email",
    label: "Email",
    searchable: true,
    sortable: true,
  },
  {
    field: "businessRelationship",
    label: "Business relationship",
    searchable: true,
    sortable: true,
  },
  {
    field: "statusText",
    label: "Status",
    searchable: true,
    sortable: true,
  },
  {
    field: "emailsSent",
    label: "Emails sent",
    searchable: true,
    sortable: true,
  },
]);
</script>
